import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { FormikProvider, useField, useFormik } from 'formik';
import { useUpdateEffect } from 'react-use';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';
import { useDebouncedCallback } from 'use-debounce';

import {
	Button,
	ContextMenu,
	Empty,
	Heading,
	Reorder,
	Skeleton,
	Subheading,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import { GoalChecklistItemType } from 'generated/graphql';

import SubgoalCard from './SubgoalCard';
import CreateSubgoalCard from './CreateSubgoalCard';
import PlusIcon from 'packages/kakapo-ui-kit/assets/icons/PlusIcon';
import { CashIcon, CircleCheckmarkIcon, RulerIcon, TargetIcon } from '@treams/treams-icons';

const CreateSubgoalButton = styled(Flex)`
	border-radius: 5px;
	border: 1px dashed ${(props) => props.theme.colors.lightBlue};
	font-size: 16px;
	color: ${(props) => props.theme.colors.slateBlue};
	align-items: center;
	cursor: pointer;

	svg {
		display: block;

		* {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}

	&:hover {
		background: white;
		border-style: solid;

		&:active {
			background: ${(props) => props.theme.colors.whiteBlueLight};
		}
	}
`;

function CreateSubgoalTypeMenu(props: { onClick: (value: GoalChecklistItemType) => void }) {
	const { t } = useTranslation('', { keyPrefix: 'subgoals.add.type' });

	const buttonData = [
		{
			title: t('checklist.title'),
			description: t('checklist.description'),
			icon: <CircleCheckmarkIcon />,
			value: GoalChecklistItemType.Checkbox,
		},
		{
			title: t('currency.title'),
			description: t('currency.description'),
			icon: <CashIcon />,
			value: GoalChecklistItemType.Currency,
		},
		{
			title: t('number.title'),
			description: t('number.description'),
			icon: <RulerIcon />,
			value: GoalChecklistItemType.Range,
		},
	];

	const buttonsComponent = buttonData.map(({ title, icon, value, description }, index) => {
		return (
			<ContextMenu.Item
				key={index}
				title={<Subheading>{title}</Subheading>}
				icon={icon}
				description={description}
				hasSubtitle={true}
				onClick={() => props.onClick(value)}
			/>
		);
	});

	return <Box width={500}>{buttonsComponent}</Box>;
}

const Subgoals = Skeleton(
	function Subgoals(props: { loading?: boolean; editable?: boolean; goalCompleted?: boolean }) {
		const { t } = useTranslation();
		const [{ value: subgoalIDs }, , { setValue: setSubgoalIDs }] = useField<string[]>('subgoalIDs');

		const [creating, setCreating] = useState<graphql.GoalChecklistItemType | undefined>(undefined);

		return (
			<>
				{subgoalIDs.length > 0 ? (
					<>
						<Reorder.Container
							onReorder={async (id, index) => {
								const update = subgoalIDs.filter((match) => match !== id);
								update.splice(index, 0, id);
								setSubgoalIDs(update);
							}}
						>
							{subgoalIDs.map((id, index) =>
								props.editable ? (
									<Reorder key={id} id={id} index={index} mb={3} loading={false}>
										<SubgoalCard
											subgoalID={id}
											editable={props.editable}
											goalCompleted={props.goalCompleted}
										/>
									</Reorder>
								) : (
									<Box key={id} mb={3}>
										<SubgoalCard
											subgoalID={id}
											editable={props.editable}
											goalCompleted={props.goalCompleted}
										/>
									</Box>
								),
							)}
						</Reorder.Container>
						{props.editable &&
							(creating ? (
								<CreateSubgoalCard type={creating} onDismiss={() => setCreating(undefined)} />
							) : (
								<ContextMenu.Target
									menu={<CreateSubgoalTypeMenu onClick={(newValue) => setCreating(newValue)} />}
								>
									<CreateSubgoalButton p={3}>
										<Box mr={2} flexShrink={0} flexGrow={0}>
											<PlusIcon />
										</Box>
										<Box>{t('subgoals.list.add.button')}</Box>
									</CreateSubgoalButton>
								</ContextMenu.Target>
							))}
					</>
				) : (
					<Box>
						{props.editable ? (
							creating ? (
								<CreateSubgoalCard type={creating} onDismiss={() => setCreating(undefined)} />
							) : (
								<Skeleton.Empty
									contents={
										<ContextMenu.Target
											menu={<CreateSubgoalTypeMenu onClick={(newValue) => setCreating(newValue)} />}
										>
											<Button type="primary" title={t('subgoals.create.save.button')} mr={3} />
										</ContextMenu.Target>
									}
								>
									<Skeleton.Repeat count={2}>
										<Box mb={3}>
											<SubgoalCard.Skeleton />
										</Box>
									</Skeleton.Repeat>
								</Skeleton.Empty>
							)
						) : (
							<Skeleton.Empty
								contents={<Empty icon={<TargetIcon />} title={t('subgoals.list.empty.title')} />}
							>
								<Skeleton.Repeat count={2}>
									<Box mb={3}>
										<SubgoalCard.Skeleton />
									</Box>
								</Skeleton.Repeat>
							</Skeleton.Empty>
						)}
					</Box>
				)}
			</>
		);
	},
	function SubgoalsSkeleton() {
		const { t } = useTranslation();

		return (
			<>
				<Heading mb={3}>{t('subgoals.list.title')}</Heading>
				<Skeleton.Repeat count={3}>
					<SubgoalCard.Skeleton mb={3} />
				</Skeleton.Repeat>
			</>
		);
	},
);

export function SubgoalsContext(props: { goal: graphql.Goal; editable?: boolean }) {
	const formik = useFormik({
		initialValues: {
			subgoals: _.chain(props.goal.checklistItems).keyBy('id').value(),
			subgoalIDs: props.goal.checklistItems.map(({ id }) => id),
		},
		onSubmit: () => {},
	});

	const [commit, { loading }] = graphql.useUpdateIndividualGoalMutation();

	const update = useDebouncedCallback(
		async () => {
			await commit({
				variables: {
					input: {
						id: props.goal.id,
						title: props.goal.title,
						description: props.goal.description,
						dueDate: props.goal.dueDate,
						linkedGoalDriverIDs: props.goal.linkedGoalDrivers?.map(({ id }) => id) ?? [],
						linkedGoalIDs: props.goal.linkedGoals?.map(({ id }) => id) ?? [],
						feasibility: props.goal.feasibility ?? undefined,
						competency: props.goal.competency
							? {
									competencyID: props.goal.competency!.competency.id,
									competencyLevelIndex: props.goal.competency!.competencyLevelIndex,
							  }
							: undefined,
						checklistItems: formik.values.subgoalIDs.map((id) => ({
							..._.pick(formik.values.subgoals[id], ['id', 'title', 'description', 'dueDate']),
							progress: {
								type: formik.values.subgoals[id].progress.type,
								value: formik.values.subgoals[id].progress.value,
								lower: formik.values.subgoals[id].progress.lower ?? undefined,
								upper: formik.values.subgoals[id].progress.upper ?? undefined,
							},
						})),
						visibility: props.goal.visibility,
					},
				},
			});
		},
		1000,
		{ leading: true },
	);

	useUpdateEffect(() => {
		update();
	}, [JSON.stringify(formik.values)]);

	return (
		<FormikProvider value={formik}>
			<Subgoals
				loading={loading}
				editable={props.editable}
				goalCompleted={!!props.goal.completedAt}
			/>
		</FormikProvider>
	);
}

export default Subgoals;
